import {
  wearsSteampack,
  CHARACTER,
  ITEMS,
  T,
  count,
  fuelToStoke,
  furnaceCapacity,
  holdDirection,
  canSpin,
  holdingOn,
  bandWidth,
  nearestStream,
  nextStar,
  sharedAt,
  STREAM,
  atHelmOrTurret,
  isClimbable,
  isHelm,
  isMachine,
  isPartOf,
  isTurretSeat,
  plantsNear,
  playerWorldCenter,
  tileAt,
  tileNear,
  waterAt,
  type Inventory,
  type PlayerState,
  type ShipState,
  type Sim,
  type Vec2,
} from "@dyson/shared";
import type { TouchSituation } from "../input/Touch";
import type { CharacterMode } from "../render/CharacterMesh";

/** The status line's keys and clicks, as the touch controls' buttons and taps (see TouchControls). In order: longer phrases first. */
const TOUCH_WORDS: [RegExp, string][] = [
  [/W\/S up\/down · A\/D back\/forward · Q\/E turn/, "stick lifts, sinks, backs and goes forward · ↺ ↻ turn"],
  [/leave the helm \(right click\)/g, "leave the helm (Leave helm)"],
  [/lets go \(right click\)/g, "lets go (Let go)"],
  [/right click lets go of the line/g, "Let go frees the line"],
  [/right click to climb out/g, "Climb out to leave"],
  [/right click to leave/g, "Leave helm to leave"],
  [/Right click the (helm|turret|harpoon) to /g, "Tap the $1 (placing) to "],
  [/right click the furnace to /g, "tap the furnace (placing) to "],
  [/hold Space to brace, aim, let go to push off/g, "hold Leap, aim with the stick and let go"],
  [/aim with the mouse · let go of Space to push off/g, "aim with the stick · let go of Leap"],
  [/WASD crawls along the rock/g, "the stick crawls along the rock"],
  [/WASD crawls along the floor/g, "the stick crawls along the floor"],
  [/hold W to reel in/g, "hold Leap to reel in"],
  [/WASD crawls along it/g, "the stick crawls along it"],
  [/ · G drops your /g, " · Throw puts down your "],
  [/ · G throws your /g, " · Throw sends your "],
  [/put something in your hand and G throws it/g, "put something in your hand and Throw sends it"],
  [/right click the flames/g, "tap the flames (placing)"],
  [/Right click to /g, "Tap (placing) to "],
  [/\(right click\)/g, "(tap, placing)"],
  [/aim with the mouse · click to fire/g, "drag to aim · Fire fires"],
  [/aim with the mouse · click fires/g, "drag to aim · Fire fires"],
  [/click and hold to reel in/g, "hold Fire to reel in"],
  [/click fires the bow guns/g, "Fire fires the bow guns"],
  [/right click, or brace, to let go/g, "a placing tap or Leap to let go"],
  [/right click throws/g, "a placing tap throws"],
  [/WASD moves you/g, "the stick moves you"],
  [/WASD near a rope/g, "the stick near a rope"],
  [/WASD flies you/g, "the stick flies you"],
  [/WASD to swim/g, "the stick swims"],
  [/Q\/E spin you round/g, "↺ ↻ spin you round"],
  [/Q\/E spin/g, "↺ ↻ spin you"],
];

/** `text` with its keys and clicks named as the touch controls have them. */
export function touchWording(text: string): string {
  for (const [re, to] of TOUCH_WORDS) text = text.replace(re, to);
  return text;
}

/** Slower than this, off the rock, we're adrift (and not just leaping across). */
const ADRIFT_SPEED = 3.5;

/** What the touch controls need to know about where we are. */
export function touchSituation(me: PlayerState, sim: Sim): TouchSituation {
  const c = playerWorldCenter(me, sim);
  const free = !me.frame && !me.vine && !waterAt(sim, c);
  const ship = me.frame ? sim.ships.get(me.frame) : undefined;
  const loose = free && !me.grounded && me.gunning === null && !holdingOn(me, sim);
  return {
    lineOut: me.gunning !== null && ship?.state.tether?.anchor === me.gunning,
    // Something in hand: Throw puts it down wherever you are (see DROPS). Off the rock it's also a push (see THROW); on it, the rock takes the push.
    canThrow: !me.vine && me.gunning === null && !waterAt(sim, c) && !!me.holding,
    adrift: loose && Math.hypot(me.velocity.x, me.velocity.y) < ADRIFT_SPEED && !wearsSteampack(me.gear) && !me.grapple,
    canTurn: canSpin(me, sim),
  };
}

/**
 * The status line under the screen: what you're doing and the keys for it.
 * `flown` is the predicted state of the ship you're piloting, if you are.
 */
export function statusText(me: PlayerState | undefined, sim: Sim | undefined, flown: ShipState | undefined, inventory: Inventory): string {
  if (!me || !sim) return "";
  return statusLine(me, sim, flown, inventory);
}

function statusLine(me: PlayerState, sim: Sim, flown: ShipState | undefined, inventory: Inventory): string {
  if (me.gunning !== null) {
    const ship = me.frame ? sim.ships.get(me.frame) : undefined;
    if (!ship || !isMachine(ship.grid.tiles[me.gunning]!, "harpoon")) return "In the turret · aim with the mouse · click to fire · right click to climb out";
    const t = ship.state.tether;
    if (!t) return "At the harpoon · aim with the mouse · click fires a line along the barrel: into rock, a whale, a gas bag or a ship · right click to climb out";
    if (t.anchor !== me.gunning) return "Another harpoon's line is out · right click to climb out";
    const what = t.creature ? "Harpooned something alive!" : t.ship ? "Hooked a ship: towing it" : "The line's fast in rock";
    return `${what} · click and hold to reel in · right click lets go of the line`;
  }
  if (me.piloting) {
    const body = flown && sim.ships.get(flown.id);
    if (body && !body.stats.furnaces) return "No furnace aboard: build one to make steam · right click to leave";
    if (flown && flown.fuel <= 0) return "Out of fuel! · leave the helm (right click) and feed the furnace coal, starbark or wood";
    if (flown && flown.water <= 0) return "Out of water! · leave the helm (right click) and feed the furnace water";
    const guns = body?.stats.bowGuns.length ? " · click fires the bow guns" : "";
    const anchor = flown?.tether ? " · on a harpoon line: whoever mans it lets go (right click)" : "";
    return `At the helm · W/S up/down · A/D back/forward · Q/E turn${guns}${anchor} · right click to leave`;
  }
  if (me.grapple) return "Hooked! · hold W to reel in · right click, or brace, to let go";
  // Standing by a furnace with room for more: say what it has and how to feed it.
  const c0 = playerWorldCenter(me, sim);
  const furnace = tileNear(sim, c0, "furnace", CHARACTER.reach)?.ship;
  if (furnace) {
    const cap = furnaceCapacity(furnace);
    const pct = (n: number) => Math.round((n / cap) * 100);
    const levels = `Furnace: fuel ${pct(furnace.state.fuel)}% · water ${pct(furnace.state.water)}%`;
    const fuel = furnace.state.fuel < cap ? fuelToStoke(inventory) : undefined;
    const water = furnace.state.water < cap && count(inventory, "water") > 0;
    if (fuel || water) return `${levels} · right click the furnace to feed ${[fuel && ITEMS[fuel].name.toLowerCase(), water && "water"].filter(Boolean).join(" and ")}`;
    if (furnace.state.fuel < cap || furnace.state.water < cap) return `${levels} · you need coal, starbark or wood, and water (dig it up), to feed it`;
  }
  const ship = me.frame ? sim.ships.get(me.frame) : undefined;
  if (ship) {
    const t = tileAt(ship.grid, Math.floor(me.position.x), Math.floor(me.position.y + CHARACTER.height / 2));
    if (isHelm(t)) return ship.state.pilotId && ship.state.pilotId !== me.id ? "Someone else is at the helm" : "Right click the helm to take it";
    // Head up in a turret's seat (climbing the ladder into it).
    const head = tileAt(ship.grid, Math.floor(me.position.x), Math.floor(me.position.y + CHARACTER.height - 0.4));
    if (isTurretSeat(head) || isTurretSeat(t)) return isMachine(head, "harpoon") || isMachine(t, "harpoon") ? "Right click the harpoon to man it" : "Right click the turret to man it";
    if (ship.state.fires) return "Fire aboard! · right click the flames holding water to put them out";
    const aboard = ship.stats.helms ? "Aboard a ship" : "Aboard drifting wreckage";
    if ((me.leap ?? 0) > 0) return "Braced · aim with the mouse · let go of Space to push off";
    if (holdingOn(me, sim)) return aboard + " · zero-g: WASD crawls along the floor, walls and ceiling · hold Space to brace, aim, let go to push off";
    return aboard + " · zero-g: floating free, WASD paddles you slowly · Q/E spin";
  }
  // A turret built on an asteroid.
  if (atHelmOrTurret(me, sim)) return "Right click the turret to man it";
  const c = c0;
  const where = onStream(sim, c);
  return where ? `${where} · ${outside(me, sim, c)}` : outside(me, sim, c);
}

/**
 * Which stream we're in the band of, if any (see twin_streams_plan.md): its name, and on a stretch beside
 * another, that stretch's name and the other's (flowing the other way, a hop across); then the next star along.
 */
function onStream(sim: Sim, c: { x: number; y: number }): string | undefined {
  const streams = sim.world.streams;
  const near = nearestStream(streams, c);
  if (!near || near.d > bandWidth(near.route, near.s) / 2) return undefined;
  const { route, s } = near;
  const beside = sharedAt(route, s);
  const next = nextStar(route, s);
  return [
    `On ${route.name}`,
    beside && `${beside.name}: ${streams![beside.with]!.name} runs beside, the other way`,
    next && `${next.place.name} in ${Math.max(1, Math.round(next.ahead / STREAM.speed / 60))} min`,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Out of any ship: holding onto rock, swimming, braced, pushed off or adrift. */
function outside(me: PlayerState, sim: Sim, c: { x: number; y: number }): string {
  if (waterAt(sim, c)) return "Swimming · WASD to swim";
  const pack = wearsSteampack(me.gear);
  const grapple = me.holding === "grapple" ? " · right click throws the grapple at rock or a ship" : "";
  if ((me.leap ?? 0) > 0) return "Braced · aim with the mouse · let go of Space to push off";
  if (me.grounded || holdingOn(me, sim)) {
    const quick = plantsNear(sim, c) ? "" : " (bare rock: slow going, quicker where things grow)";
    const put = me.holding ? ` · G drops your ${ITEMS[me.holding].name.toLowerCase()} where you are` : "";
    return `Holding on${quick} · WASD crawls along the rock, up its walls and round its underside · hold Space to brace, aim, let go to push off${grapple}${put}`;
  }
  const thrown = me.holding ? ` · G throws your ${ITEMS[me.holding].name.toLowerCase()} and pushes you the other way (it hangs there to be picked up)` : " · put something in your hand and G throws it, pushing you the other way";
  if (pack) return "In open space · WASD flies you on the steampack · Q/E spin you round" + (me.holding === "grapple" ? " · right click throws the grapple at rock or a ship" : " · R starts you over on the stream");
  return (
    "Adrift in open space · no steampack: nothing to push on but what you carry" +
    (me.holding === "grapple" ? " · right click throws the grapple at rock or a ship within reach" : "") +
    thrown +
    " · R starts you over on the stream"
  );
}

/**
 * Which animation a player gets, and which way what they hold lies in their
 * own frame (see holdDirection) for the crawl pose to reach that way: at the
 * helm, on a ladder, swimming, holding onto rock, drifting, or walking a
 * ship's deck.
 */
export function characterMode(p: PlayerState, sim: Sim): { mode: CharacterMode; grip: Vec2 | null } {
  const plain = (mode: CharacterMode) => ({ mode, grip: null });
  if (p.piloting || p.gunning !== null) return plain("helm");
  if (p.vine) return plain("climb");
  const ship = p.frame ? sim.ships.get(p.frame) : undefined;
  const c = playerWorldCenter(p, sim);
  let tile: number = T.EMPTY;
  if (ship) tile = tileAt(ship.grid, Math.floor(p.position.x), Math.floor(p.position.y + CHARACTER.height / 2));
  else for (const a of sim.asteroids) tile ||= tileAt(a.grid, Math.floor(c.x - a.def.ox), Math.floor(c.y - a.def.oy));
  if (tile === T.WATER) return plain("swim");
  if (isClimbable(tile) && !p.grounded) return plain("climb");
  // Out in the world nobody stands: holding onto rock, you hang off it by your hands, wherever it is (see GRIP).
  if (!ship) {
    const grip = holdDirection(p, sim);
    return grip ? { mode: "crawl", grip } : plain("drift");
  }
  if (p.grounded) return plain("ground");
  // Zero-g aboard a ship.
  return plain("drift");
}
